import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { API_ERROR_CODES, isApiError } from '../../../shared/http/api-error.js';
import { API_UNAUTHENTICATED_EVENT } from '../../../shared/http/transport.js';
import { INTEGRATION_POLL_MS } from '../../../shared/domain/integrations/poll-interval.js';
import { useRevalidateOnVisible } from '../../app/hooks/use-revalidate-on-visible.js';
import { enableClientErrorDelivery, reportClientError } from '../../app/errors/report-client-error.js';
import { notifyUser } from '../../ui/ToastViewport.jsx';
import * as authApi from './api/auth.api.js';
import { SessionContext } from './session-context.js';

const LAST_DISPLAY_NAME_STORAGE_KEY = 'eaxfuel.lastDisplayName';
const rememberDisplayName = (displayName) => {
  try {
    if (displayName) globalThis.localStorage?.setItem(LAST_DISPLAY_NAME_STORAGE_KEY, displayName);
    else globalThis.localStorage?.removeItem(LAST_DISPLAY_NAME_STORAGE_KEY);
  } catch (_error) {
    // This value is only a visual startup hint; authentication always comes from the server.
  }
};

/**
 * Tells every other tab of this browser to re-check the session, the moment this tab
 * signs out.
 *
 * A `storage` write is the standard way to reach other same-origin tabs instantly, no
 * new channel to manage. It never fires in the tab that wrote it, which is fine: that
 * tab already updated its own state directly.
 */
const SESSION_SIGNAL_STORAGE_KEY = 'eaxfuel.sessionSignal';
const broadcastSessionChange = () => {
  try { globalThis.localStorage?.setItem(SESSION_SIGNAL_STORAGE_KEY, String(Date.now())); }
  catch (_error) {
    // Best-effort only: another tab still recovers on its own next request that 401s.
  }
};

/**
 * Owns who is signed in.
 *
 * The session is established by asking the server, never by reading local storage. The
 * old app decided it was signed in by reading `{ loggedIn: true }` from localStorage,
 * which anyone could type into devtools, and since the server checked nothing it was
 * the whole security model.
 *
 * Three states and no ambiguity: `loading` until the first answer arrives, then
 * `signed-in` or `signed-out`. Nothing renders the dashboard while the answer is
 * unknown, and a failed check is not silently treated as signed out.
 *
 * Audit references prevented here: auth.jsx #1, App.jsx #18, main.jsx #1.
 *
 * @param {{children: import('react').ReactNode}} props
 */
export function SessionProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', user: null, session: null, error: null, retrying: false });
  const inFlight = useRef(null);

  /**
   * Ask the server who is signed in.
   *
   * An UNAUTHENTICATED answer is the normal signed-out case, not an error, so it is
   * not reported. Anything else is a real failure and is surfaced with a retry rather
   * than silently becoming a sign-in screen.
   */
  const refresh = useCallback(async (signal) => {
    try {
      const { user, session } = await authApi.fetchCurrentSession({ signal });
      setState({ status: 'signed-in', user, session, error: null, retrying: false });
      rememberDisplayName(user?.displayName);
      enableClientErrorDelivery(true);
      return true;
    } catch (error) {
      if (isApiError(error) && error.code === API_ERROR_CODES.UNAUTHENTICATED) {
        setState({ status: 'signed-out', user: null, session: null, error: null, retrying: false });
        rememberDisplayName(null);
        enableClientErrorDelivery(false);
        return true;
      }
      if (signal?.aborted) return true;
      reportClientError(error, { scope: 'auth.refresh' });
      const isTemporaryNetworkFailure = isApiError(error) && error.code === API_ERROR_CODES.NETWORK;
      if (isTemporaryNetworkFailure) {
        // A deploy must not throw an authenticated user onto the sign-in screen. Keep
        // their current app visible; on first load retain the branded loading screen.
        // `retrying` is what tells that screen apart from the very first attempt, so
        // "loading" does not sit there unchanged through several failed retries with
        // nothing on screen to say a retry is even happening.
        setState((current) => current.status === 'signed-in'
          ? current
          : { ...current, status: 'loading', error: null, retrying: true });
        return false;
      }
      setState({ status: 'signed-out', user: null, session: null, retrying: false,
        error: isApiError(error) ? error.message : 'Could not reach the server.' });
      return true;
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const wait = (ms) => new Promise((resolve) => {
      const timer = setTimeout(resolve, ms);
      controller.signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
    });
    const establishSession = async () => {
      while (!controller.signal.aborted) {
        const settled = await refresh(controller.signal);
        if (settled || controller.signal.aborted) return;
        await wait(5_000);
      }
    };
    void establishSession();
    return () => controller.abort();
  }, [refresh]);

  // Any request anywhere - a feature load, a save, not just the initial check - can
  // come back 401 because a device was revoked, a password change ended other
  // sessions, or this session simply expired. Without this, only the first load ever
  // reacted to UNAUTHENTICATED; everything after it just showed its own feature-level
  // error while the rest of the app kept rendering as though still signed in. Trusted
  // directly, no re-check: a 401 is already the server's answer.
  useEffect(() => {
    const onUnauthenticated = () => {
      setState((current) => current.status === 'signed-out' ? current : { status: 'signed-out', user: null, session: null, error: null, retrying: false });
      rememberDisplayName(null);
      enableClientErrorDelivery(false);
    };
    globalThis.addEventListener(API_UNAUTHENTICATED_EVENT, onUnauthenticated);
    return () => globalThis.removeEventListener(API_UNAUTHENTICATED_EVENT, onUnauthenticated);
  }, []);

  // A sibling tab signing out writes the signal below; this re-asks the server rather
  // than assuming signed-out, since that is the one true source for what changed.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== SESSION_SIGNAL_STORAGE_KEY || !event.newValue) return;
      void refresh();
    };
    globalThis.addEventListener('storage', onStorage);
    return () => globalThis.removeEventListener('storage', onStorage);
  }, [refresh]);

  // A device revoked, or a session ended by a password change elsewhere, only shows up
  // through the two listeners above once this tab makes another request - and a tab
  // left in the background may not make one for a long time, nor does a tab that is
  // simply left open and visible without the user ever touching it again. Re-checking
  // on visibility regain and on a fixed interval the rest of the time catches both.
  useRevalidateOnVisible(refresh, { intervalMs: INTEGRATION_POLL_MS });

  /**
   * Run a sign-in or sign-up call, then confirm the session with the server.
   *
   * The state is not set from the login response alone: the server is asked again, so
   * what the app believes about the session is always what the server said, including
   * the expiry and the days remaining.
   *
   * Concurrent submissions are collapsed, so a double-tapped button issues one call.
   */
  const runAndConfirm = useCallback(
    async (operation) => {
      if (inFlight.current) return inFlight.current;
      const promise = (async () => {
        try {
          await operation();
          await refresh();
        } finally {
          inFlight.current = null;
        }
      })();
      inFlight.current = promise;
      return promise;
    },
    [refresh],
  );

  const signIn = useCallback(
    (credentials) => runAndConfirm(() => authApi.login(credentials)),
    [runAndConfirm],
  );

  const signUp = useCallback(
    (details) => runAndConfirm(() => authApi.register(details)),
    [runAndConfirm],
  );

  const signOut = useCallback(async () => {
    let serverSessionEnded = true;
    try {
      await authApi.logout();
    } catch (error) {
      serverSessionEnded = false;
      // The local session is cleared either way: staying on a dashboard the user
      // asked to leave is worse than a stale server-side session, which expires and
      // can be revoked from any other device.
      reportClientError(error, { scope: 'auth.signOut' }, 'warning');
    }
    setState({ status: 'signed-out', user: null, session: null, error: null });
    rememberDisplayName(null);
    enableClientErrorDelivery(false);
    broadcastSessionChange();
    notifyUser(serverSessionEnded ? 'Successfully signed out.' : 'Signed out on this device.', serverSessionEnded ? 'success' : 'info');
  }, []);

  const value = useMemo(
    () => ({ ...state, refresh: () => refresh(), signIn, signUp, signOut }),
    [state, refresh, signIn, signUp, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
